import {
  collection,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getCountFromServer
} from "firebase/firestore";
import { db } from "@/services/firebaseService";
import type { LocationAccessGroup } from "@/types/locationAccessGroup";

export async function getLocationAccessGroupById(groupId: string): Promise<LocationAccessGroup | null> {
  const snap = await getDoc(doc(db, "locationAccessGroups", groupId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as any) } as LocationAccessGroup;
}

export async function listLocationAccessGroupsByOrganization(organizationId: string): Promise<LocationAccessGroup[]> {
  const q = query(collection(db, "locationAccessGroups"), where("organizationId", "==", organizationId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as LocationAccessGroup[];
}

export async function createLocationAccessGroup(input: {
  name: string;
  organizationId: string;
  locationIds: string[];
  lastEditedBy: string;
  purpose?: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, "locationAccessGroups"), {
    ...input,
    createdAt: serverTimestamp(),
    lastEditedAt: serverTimestamp()
  });
  return ref.id;
}

export async function updateLocationAccessGroup(
  groupId: string,
  patch: Partial<Pick<LocationAccessGroup, "name" | "locationIds" | "purpose" | "lastEditedBy">>
): Promise<void> {
  await updateDoc(doc(db, "locationAccessGroups", groupId), {
    ...patch,
    lastEditedAt: serverTimestamp()
  } as any);
}

export async function deleteLocationAccessGroup(groupId: string): Promise<void> {
  await deleteDoc(doc(db, "locationAccessGroups", groupId));
}

/**
 * Counts users assigned to a location access group, scoped to the org that
 * owns the group.
 *
 * The `organizationId` filter is not cosmetic — it is what makes the query
 * legal. The KB-2291 `users/{userId}` read rule admits a non-admin only via
 * `isFullOrgUser(resource.data.organizationId)`, and Firestore evaluates a LIST
 * against the QUERY, so the caller must prove every matched doc is in their own
 * org. Filtering on `locationAccessGroupId` alone proves nothing and is denied
 * for everyone below admin_user (KB-2306). Two equality filters need no
 * composite index — Firestore serves them by merging single-field indexes.
 *
 * Note this still cannot succeed for a caller who is themselves confined to a
 * location access group: `isFullOrgUser()` requires
 * `getLocationAccessGroupId() == null`, so the narrowest staff tier may not
 * read peer user docs at all. That denial propagates to the caller rather than
 * being reported as a count of zero.
 */
export async function getAssignedUserCountForGroup(
  groupId: string,
  organizationId: string
): Promise<number> {
  const q = query(
    collection(db, "users"),
    where("organizationId", "==", organizationId),
    where("locationAccessGroupId", "==", groupId)
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

